// Pure helpers for the run-card simulation-progress surface.
//
// While a run integrates, the backend stamps status_detail like
//   "simulating <U+2014> 14.2 / 28.0 s"
// (a genuine em-dash separator, U+2014). parseSimProgress lifts the sim-time
// and window out of that string so the run card can draw a determinate
// progress bar plus an ETA. Other stage strings ("simulating <U+2014> full lap
// replay", "simulating <U+2014> 20 s window", spec/validate stages) do not
// carry an "X / Y s" pair and parse to null, so the card keeps its plain
// status-detail text for them.

export type SimProgress = {
  simS: number;
  durationS: number;
};

// Tolerant of the em-dash (U+2014), en-dash (U+2013) and a plain hyphen so a
// future backend copy tweak does not silently break the bar.
const SIM_PROGRESS_RE =
  /simulating\s*[—–-]\s*([0-9]+(?:\.[0-9]+)?)\s*\/\s*([0-9]+(?:\.[0-9]+)?)\s*s\b/i;

export function parseSimProgress(
  statusDetail: string | null | undefined,
): SimProgress | null {
  if (!statusDetail) return null;
  const m = SIM_PROGRESS_RE.exec(statusDetail);
  if (!m) return null;
  const simS = Number(m[1]);
  const durationS = Number(m[2]);
  if (!Number.isFinite(simS) || !Number.isFinite(durationS) || durationS <= 0) {
    return null;
  }
  return { simS, durationS };
}

export type SimObservation = {
  tMs: number; // wall-clock timestamp of the observation
  x: number; // sim-time observed at that instant
};

// Estimate wall-seconds remaining from the observed sim-time rate:
//   rate = (x_last - x_first) / (t_last - t_first)   [sim-s per wall-s]
//   eta  = (duration - x_last) / max(rate, epsilon)
// Returns null until there are at least two observations, or if the rate is
// not yet positive (no forward progress seen). Returns 0 once the window is
// covered.
export function estimateEtaSeconds(
  observations: SimObservation[],
  durationS: number,
): number | null {
  if (observations.length < 2) return null;
  const first = observations[0];
  const last = observations[observations.length - 1];
  const dtS = (last.tMs - first.tMs) / 1000;
  const dx = last.x - first.x;
  if (dtS <= 0 || dx <= 0) return null;
  const remaining = durationS - last.x;
  if (remaining <= 0) return 0;
  const rate = dx / dtS;
  const EPS = 1e-6;
  return remaining / Math.max(rate, EPS);
}

export function formatEtaSeconds(etaS: number | null): string {
  if (etaS == null || !Number.isFinite(etaS)) return "";
  return `~${Math.round(etaS)} s left`;
}
